import React, { FC, useMemo, useState } from 'react';
import { ReactComponent as CancelTripIcon } from 'assets/icons/cancel-trip-modal-icon.svg';
import { ReactComponent as WalletIcon } from 'assets/icons/empty-wallet.svg';
import { ReactComponent as BankIcon } from 'assets/icons/bank.svg';
import { toast } from 'react-toastify';
import {
  Button,
  Input,
  ToastMessage,
  BaseModal,
  FileUploader
} from '@cokitchen/cokitchen-components';
import { OrderType } from 'models/order.model';
import { apiService } from 'utils/api';
import { handleApiError } from 'utils/functions';
import { STORAGE_KEYS } from 'utils/constants';

const CANCEL_POSTPAID_ORDER_REASONS = [
  'Wrong Delivery Address',
  'Wrong Order Delivered',
  'Spilled in Transit',
  'Duplicate Payment',
  'Incomplete Items',
  'Food Quality',
  'Partially Wrong Order',
  'Wrong Order Packed'
];

type RefundType = 'full' | 'partial';
type RefundMethod = 'wallet' | 'bank';

interface CancelPostpaidOrderModalProps {
  show: boolean;
  onClose: () => void;
  order: OrderType;
}

interface BankDetails {
  accountNumber: string;
  accountName: string;
  bankName: string;
}

const INITIAL_BANK_DETAILS: BankDetails = {
  accountNumber: '',
  accountName: '',
  bankName: ''
};

const SelectableCard: FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
  showIndicator?: boolean;
}> = ({ active, onClick, children, className = '', showIndicator }) => (
  <div
    onClick={onClick}
    className={`rounded-xl border cursor-pointer transition-all ${
      active ? 'border-[#FA8596] bg-red-9' : 'border-gray-border hover:border-gray-3'
    } ${className}`}>
    {showIndicator ? (
      <div className='flex items-center justify-between gap-3'>
        {children}
        <div
          className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
            active ? 'border-red-5' : 'border-gray-3'
          }`}>
          {active && <div className='w-2.5 h-2.5 rounded-full bg-red-5' />}
        </div>
      </div>
    ) : (
      children
    )}
  </div>
);

const CancelPostpaidOrderModal: FC<CancelPostpaidOrderModalProps> = ({ show, onClose, order }) => {
  const orderCode = order?.order_code?.toUpperCase() ?? '';

  const [loading, setLoading] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [refundType, setRefundType] = useState<RefundType | ''>('');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundMethod, setRefundMethod] = useState<RefundMethod | ''>('');
  const [bankDetails, setBankDetails] = useState<BankDetails>(INITIAL_BANK_DETAILS);

  const maxRefundAmount = useMemo(
    () => parseFloat(order?.calculated_order?.total_amount ?? '0'),
    [order]
  );

  const isFormValid = useMemo(() => {
    if (!selectedReason) return false;
    if (!refundType || !refundMethod) return false;
    if (refundType === 'partial' && (!refundAmount || parseFloat(refundAmount) <= 0)) return false;
    if (refundMethod === 'bank') {
      const { accountNumber, accountName, bankName } = bankDetails;
      if (!accountNumber || !accountName || !bankName) return false;
    }
    return true;
  }, [selectedReason, refundType, refundAmount, refundMethod, bankDetails]);

  const resetForm = () => {
    setSelectedReason('');
    setImageUrls([]);
    setRefundType('');
    setRefundAmount('');
    setRefundMethod('');
    setBankDetails(INITIAL_BANK_DETAILS);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    const payload = {
      order_code: order.order_code,
      cancelled_note: selectedReason,
      refund_type: refundType,
      refund_amount: refundType === 'partial' ? refundAmount : maxRefundAmount.toString(),
      refund_method: refundMethod,
      ...(refundMethod === 'bank' && {
        account_number: bankDetails.accountNumber,
        account_name: bankDetails.accountName,
        bank_name: bankDetails.bankName
      }),
      image: imageUrls
    };

    try {
      setLoading(true);
      await apiService.post('logistics/order/cancel', {
        data: payload,
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      toast(<ToastMessage type='success' message='Trip cancelled successfully' />);
      handleClose();
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const updateBankDetail = (field: keyof BankDetails, value: string) => {
    setBankDetails((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <BaseModal
      isOpen={show}
      position='right'
      title='Cancel this trip'
      size='wide'
      className='w-[70vw] max-w-[unset]'
      description={`Trip #${orderCode}`}
      titleIcon={<CancelTripIcon />}
      titleIconColor='danger'
      onRequestClose={handleClose}
      footer={
        <div className='grid grid-cols-2 gap-x-4'>
          <Button small color='primary' variant='outline' className='mx-auto' onClick={handleClose}>
            Cancel
          </Button>
          <Button
            small
            color='primary'
            variant='block'
            className='mx-auto'
            loading={loading}
            disabled={!isFormValid}
            onClick={handleSubmit}>
            Confirm
          </Button>
        </div>
      }>
      <section>
        <h4 className='text-base font-medium text-[#808080] mb-3'>Select cancellation reason</h4>
        <div className='grid gap-6 grid-cols-2 mt-2'>
          {CANCEL_POSTPAID_ORDER_REASONS.map((reason, i) => (
            <Input
              key={i}
              value={selectedReason}
              label={reason}
              type='radio'
              onChange={() => setSelectedReason(reason)}
              name='cancel-reason'
            />
          ))}
        </div>
      </section>

      <section className='mt-6'>
        <label className='font-medium'>Upload Proof (Optional)</label>
        <FileUploader
          token={localStorage.getItem(STORAGE_KEYS.TOKEN) as string}
          fileUrls={imageUrls}
          setFileUrls={setImageUrls}
        />
      </section>

      <section className='mt-6'>
        <h4 className='text-sm font-medium text-gray-6 mb-3'>Select refund type</h4>
        <div className='grid grid-cols-2 gap-4'>
          <SelectableCard
            active={refundType === 'full'}
            onClick={() => {
              setRefundType('full');
              setRefundAmount('');
            }}
            className='p-4'
            showIndicator>
            <div>
              <p className='font-semibold text-sm'>Full Refund</p>
              <p className='text-xs text-gray-5 mt-1'>Return 100% of order value to customer</p>
            </div>
          </SelectableCard>

          <SelectableCard
            active={refundType === 'partial'}
            onClick={() => setRefundType('partial')}
            className='p-4'
            showIndicator>
            <div>
              <p className='font-semibold text-sm'>Partial Refund</p>
              <p className='text-xs text-gray-5 mt-1'>
                Deduct cancellation fee or specific item cost
              </p>
            </div>
          </SelectableCard>
        </div>
      </section>

      {refundType === 'partial' && (
        <section className='mt-4'>
          <Input
            label={`Enter refund amount, Max: (${maxRefundAmount.toLocaleString()})`}
            value={refundAmount}
            onChange={(e: any) => {
              const val = e.target.value.replace(/[^0-9]/g, '');
              if (!val || parseFloat(val) <= maxRefundAmount) {
                setRefundAmount(val);
              }
            }}
            placeholder='0'
            className='w-full'
          />
        </section>
      )}

      <section className='mt-6'>
        <h4 className='text-sm font-medium text-gray-6 mb-3'>Refund Method</h4>
        <div className='grid grid-cols-2 gap-4'>
          <SelectableCard
            active={refundMethod === 'wallet'}
            onClick={() => {
              setRefundMethod('wallet');
              setBankDetails(INITIAL_BANK_DETAILS);
            }}
            className='py-6 px-4'>
            <div className='flex flex-col items-center gap-2'>
              <WalletIcon className='w-8 h-8' />
              <p className='text-sm font-medium'>FC Wallet</p>
            </div>
          </SelectableCard>

          <SelectableCard
            active={refundMethod === 'bank'}
            onClick={() => setRefundMethod('bank')}
            className='py-6 px-4'>
            <div className='flex flex-col items-center gap-2'>
              <BankIcon className='w-8 h-8' />
              <p className='text-sm font-medium'>Bank transfer</p>
            </div>
          </SelectableCard>
        </div>
      </section>

      {refundMethod === 'bank' && (
        <section className='mt-4'>
          <div className='grid grid-cols-2 gap-4'>
            <Input
              label='Account Number'
              inputMode='numeric'
              value={bankDetails.accountNumber}
              onChange={(e: any) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                updateBankDetail('accountNumber', val);
              }}
              placeholder='0000000000'
            />
            <Input
              label='Account Name'
              value={bankDetails.accountName}
              onChange={(e: any) => updateBankDetail('accountName', e.target.value)}
              placeholder='Account name'
            />
          </div>
          <div className='mt-4'>
            <Input
              label='Bank Name'
              value={bankDetails.bankName}
              onChange={(e: any) => updateBankDetail('bankName', e.target.value)}
              placeholder='Select bank'
            />
          </div>
        </section>
      )}
    </BaseModal>
  );
};

export default CancelPostpaidOrderModal;
